import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Img from '../components/Img';
import Reveal from '../components/Reveal';
import useElementSize from '../hooks/useElementSize';
import { GALLERIES, KIND_LABEL, imagesFor } from '../utils/galleries';
import { justifyRows } from '../utils/layout';
import { breadcrumbSchema } from '../utils/schema';
import workContent from '../content/work.json';
import travelContent from '../content/travel.json';

const FILTERS = [
  ['all', 'All'],
  ['gallery', 'Galleries'],
  ['trip', 'Trips'],
];

// Phones: one card per row (an unreachable target height closes every row
// after one item), with portraits trimmed to 4:5 so they don't dominate.
const cardMetrics = (width) => {
  if (width < 600) return { targetHeight: Infinity, gap: 12, minAspect: 0.8 };
  if (width < 1024) return { targetHeight: 340, gap: 16, minAspect: 0 };
  return { targetHeight: 440, gap: 20, minAspect: 0 };
};

const CardGrid = ({ items }) => {
  const [ref, { width }] = useElementSize();
  const { rows, gap } = useMemo(() => {
    const metrics = cardMetrics(width);
    return {
      rows: justifyRows(
        items.map((item) => ({ ...item, aspect: Math.max(metrics.minAspect, item.cover.aspect) })),
        width,
        metrics
      ),
      gap: metrics.gap,
    };
  }, [items, width]);

  return (
    <div ref={ref} className="cards" style={{ '--gap': `${gap}px` }}>
      {rows.map((row) => (
        <div key={row.items[0].gallery.slug} className="cards__row">
          {row.items.map((item, j) => (
            <Reveal
              key={item.gallery.slug}
              className="cards__item"
              delay={j * 0.08}
              style={row.last ? { width: row.widths[j] } : { flexGrow: row.shares[j], flexBasis: 0 }}
            >
              <Link to={item.gallery.path} className="card">
                <span className="card__image" style={{ height: row.height }}>
                  <Img
                    image={item.cover}
                    alt={`${item.gallery.title} — ${KIND_LABEL[item.gallery.kind].toLowerCase()} by Jonny Coe`}
                    sizes={`${Math.ceil(row.widths[j] / 50) * 50}px`}
                  />
                </span>
                <span className="card__info">
                  <span className="card__title">{item.gallery.title}</span>
                  <span className="card__meta">
                    <span>{KIND_LABEL[item.gallery.kind]}</span>
                    <span className="card__dot" aria-hidden="true">
                      ·
                    </span>
                    <span>{item.count} frames</span>
                  </span>
                  <span className="card__blurb">{item.gallery.blurb}</span>
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      ))}
    </div>
  );
};

const IndexList = ({ items }) => {
  const [hovered, setHovered] = useState(null);
  return (
    <div className="indexList" onMouseLeave={() => setHovered(null)}>
      <ol>
        {items.map((item, i) => (
          <li key={item.gallery.slug}>
            <Link
              to={item.gallery.path}
              className="indexList__row"
              onMouseEnter={() => setHovered(item)}
              onFocus={() => setHovered(item)}
            >
              <span className="indexList__num">{String(i + 1).padStart(2, '0')}</span>
              <span className="indexList__title">{item.gallery.title}</span>
              <span className="indexList__blurb">{item.gallery.blurb}</span>
              <span className="indexList__meta">
                {KIND_LABEL[item.gallery.kind]} · {item.count}
              </span>
            </Link>
          </li>
        ))}
      </ol>
      <div className={`indexList__preview${hovered ? ' is-shown' : ''}`} aria-hidden="true">
        {items.map((item) => (
          <span
            key={item.gallery.slug}
            className="indexList__previewFrame"
            data-active={hovered === item ? '' : undefined}
            style={{ '--a': item.cover.aspect }}
          >
            <Img image={item.cover} alt="" sizes="480px" />
          </span>
        ))}
      </div>
    </div>
  );
};

/** Index of every gallery and trip. `/travel` is the same page pre-filtered to trips. */
const Work = ({ preset }) => {
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState('grid');
  const filter = preset || params.get('type') || 'all';
  const content = preset === 'trip' ? travelContent : workContent;

  const items = useMemo(
    () =>
      GALLERIES.map((gallery) => {
        const images = imagesFor(gallery.slug);
        const chosen = images.find((img) => img.stem === gallery.cover);
        return {
          gallery,
          count: images.length,
          cover: chosen || images[Math.floor(Math.random() * images.length)],
        };
      }),
    []
  );

  const counts = {
    all: items.length,
    gallery: items.filter((i) => i.gallery.kind === 'gallery').length,
    trip: items.filter((i) => i.gallery.kind === 'trip').length,
  };
  const visible = filter === 'all' ? items : items.filter((i) => i.gallery.kind === filter);

  const selectFilter = (type) => {
    if (preset) return;
    setParams(type === 'all' ? {} : { type }, { replace: true });
  };

  return (
    <main className="work">
      <SEO
        content={content}
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: content.title, path: content.path },
          ]),
        ]}
      />
      <header className="work__header">
        <div className="work__top">
          <h1 className="label work__h1">{preset === 'trip' ? 'Travel' : 'All work'}</h1>
          <div className="toggle" role="group" aria-label="View">
            {['grid', 'index'].map((v) => (
              <button
                key={v}
                type="button"
                className={`toggle__btn${view === v ? ' is-active' : ''}`}
                aria-pressed={view === v}
                onClick={() => setView(v)}
              >
                {v === 'grid' ? 'Grid' : 'Index'}
              </button>
            ))}
          </div>
        </div>
        {content.lede && <p className="work__lede">{content.lede}</p>}
        <div className="filters" role="group" aria-label="Filter">
          {FILTERS.map(([type, name]) =>
            preset && type !== 'trip' ? (
              <Link key={type} to={type === 'all' ? '/work' : `/work?type=${type}`} className="filters__btn">
                {name} <span className="filters__count">{counts[type]}</span>
              </Link>
            ) : (
              <button
                key={type}
                type="button"
                className={`filters__btn${filter === type ? ' is-active' : ''}`}
                aria-pressed={filter === type}
                onClick={() => selectFilter(type)}
              >
                {name} <span className="filters__count">{counts[type]}</span>
              </button>
            )
          )}
        </div>
      </header>

      {view === 'grid' ? <CardGrid items={visible} /> : <IndexList items={visible} />}
    </main>
  );
};

export default Work;
